import { ReactNode, useEffect, useMemo, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@atlaskit/button/new';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { Grid, GridColumn } from '@atlaskit/page';
import { xcss, Box } from '@atlaskit/primitives';

import { FocusedPageHeader } from 'src/components/accessibility';
import { useAgenticPipelinesConfiguration } from 'src/components/pipelines/hooks/use-agentic-pipelines-configuration';
import { capturePipelinesExceptionWithTags } from 'src/components/pipelines/utils/sentry';
import { useConfigBoolean } from 'src/contexts/statsig-config-provider';
import { StatsigFeatureKeys } from 'src/features';
import { useStatsigGate } from 'src/hooks/statsig';
import { showFlag } from 'src/redux/flags';
import { SYNC_AI_FEATURE_PREFS } from 'src/redux/global/actions';
import { useParams } from 'src/router/hooks';
import { PageSection } from 'src/sections/global/components/page.style';
import { AtlassianIntelligenceThingsToKnow } from 'src/sections/workspace/sections/settings/components/atlassian-intelligence/components/atlassian-intelligence-things-to-know';
import workspaceSectionUrls from 'src/sections/workspace/urls';
import {
  getIsAiRovoChatEnabled,
  getIsAiEnabled,
  getIsAiEditorFeaturesEnabled,
} from 'src/selectors/feature-selectors';
import {
  getCurrentWorkspace,
  getCurrentWorkspaceExtraAttributes,
} from 'src/selectors/workspace-selectors';
import { publishUiEvent } from 'src/utils/analytics/publish';
import { captureExceptionWithTags } from 'src/utils/sentry';

import Breadcrumbs from '../breadcrumbs';
import { PremiumFeatureUpsell } from '../premium';

import { useAnalyticsEvents } from './atlassian-intelligence-analytics';
import {
  genAtlassianIntelligenceCallout,
  getAiPreferencesCallout,
} from './atlassian-intelligence-callouts';
import { AtlassianIntelligenceDeactivationModal } from './atlassian-intelligence-deactivation-modal';
import {
  getAiToggleFlag,
  getAiToggleWritingAssistantFlag,
  getAiToggleRovoChatFlag,
  getAiToggleAgenticPipelinesFlag,
} from './atlassian-intelligence-flags';
import { AtlassianIntelligencePreferenceOnToggleListener } from './atlassian-intelligence-preference';
import { AtlassianIntelligencePreferencesScreen } from './atlassian-intelligence-preferences-screen';
import { AtlassianIntelligenceTosForm } from './atlassian-intelligence-tos-form';
import { messages } from './atlassian-intelligence.i18n';
import { Header } from './atlassian-intelligence.style';
import atlassianIntelligenceUrls from './urls';

import { WorkspaceAiPreferenceResourceDataType } from '.';

const preferenceScreenBoxStyles = xcss({
  marginTop: 'space.200',
});
const PreferenceScreenBox = ({
  children,
}: {
  children: ReactNode | ReactNode[];
}) => {
  return <Box xcss={preferenceScreenBoxStyles}>{children}</Box>;
};

export const AtlassianIntelligence = (): JSX.Element => {
  const preferences = useWorkspaceAiPreferences();
  const activated = preferences.aiEnabled;

  const {
    onAiEnabled,
    onPageViewed,
    onAiDisabled,
    onAiRovoChatToggleTrackEvent,
    onAiWritingAssistantToggleTrackEvent,
  } = useAnalyticsEvents();
  useEffect(onPageViewed, [onPageViewed]);

  const params = useParams<{
    workspaceSlug: string;
  }>();
  const intl = useIntl();
  const workspaceSlug = params.workspaceSlug || '';
  const settingsHref = workspaceSectionUrls.ui.settings(workspaceSlug);
  const { hasDefaultFree } = useSelector(getCurrentWorkspaceExtraAttributes);
  const currentWorkspace = useSelector(getCurrentWorkspace);
  const dispatch = useDispatch();

  const isAgenticWorkspaceToggleEnabled = useConfigBoolean(
    StatsigFeatureKeys.isAgenticWorkspaceToggleEnabled,
    false
  );
  const isAgenticStepsEnabled = useStatsigGate(
    StatsigFeatureKeys.isAgenticStepsEnabled
  );
  const { agenticPipelinesEnabled, updateAgenticPipelinesConfiguration } =
    useAgenticPipelinesConfiguration(workspaceSlug);

  const [isActivated, setIsActivated] = useState<boolean>(activated);

  const showUpsell = !isActivated && hasDefaultFree;
  const aiUpsellTitle = intl.formatMessage(
    messages.atlassianIntelligenceUpgradeToPaidTitle
  );

  const [isDeactivateLoading, setIsDeactivateLoading] = useState(false);

  const [isDeactivationConfirmationOpen, setIsDeactivationConfirmationOpen] =
    useState(false);

  const aiPreferencesCallout = useMemo(() => {
    return getAiPreferencesCallout(workspaceSlug);
  }, [workspaceSlug]);

  const aiCallout = useMemo(() => {
    return genAtlassianIntelligenceCallout({
      workspaceSlug,
    });
  }, [workspaceSlug]);

  const logSentryError = (e: Error) => {
    captureExceptionWithTags(e, {
      component: 'atlassian-intelligence',
    });
  };

  const [
    showAtlassianIntelligencePreferences,
    setShowAtlassianIntelligencePreferences,
  ] = useState<boolean>(activated);
  const onSubmit = async () => {
    function showAiToggleErrorFlag() {
      const action = showFlag(
        getAiToggleFlag({
          on: true,
          error: true,
          workspace: workspaceSlug,
        })
      );
      dispatch(action);
    }

    try {
      const response = await aiCallout(true);

      if (!response.ok) {
        showAiToggleErrorFlag();
        return undefined;
      }

      const contents =
        (await response.json()) as WorkspaceAiPreferenceResourceDataType;

      setIsActivated(contents.aiEnabled);
      setShowAtlassianIntelligencePreferences(true);

      if (isAgenticStepsEnabled) {
        updateAgenticPipelinesConfiguration(true);
      }

      const action = showFlag(
        getAiToggleFlag({
          on: true,
          error: false,
          workspace: workspaceSlug,
        })
      );
      dispatch(action);

      onAiEnabled(preferences, contents);

      dispatch({
        type: SYNC_AI_FEATURE_PREFS,
        payload: contents,
      });
    } catch (e) {
      logSentryError(e);
      showAiToggleErrorFlag();
    }

    return undefined;
  };

  const onAiWritingAssistantToggle: AtlassianIntelligencePreferenceOnToggleListener =
    useMemo(() => {
      return async function ({ isChecked }) {
        function showOnAiWritingAssistantFlag(error: boolean) {
          const action = showFlag(
            getAiToggleWritingAssistantFlag({
              workspace: workspaceSlug,
              on: isChecked,
              error,
            })
          );
          dispatch(action);
        }

        let response = undefined;
        try {
          response = await aiPreferencesCallout({
            aiEditorFeaturesEnabled: isChecked,
          });

          showOnAiWritingAssistantFlag(!response.ok);

          if (response.ok) {
            const contents =
              (await response.json()) as WorkspaceAiPreferenceResourceDataType;
            onAiWritingAssistantToggleTrackEvent(preferences, contents);

            dispatch({
              type: SYNC_AI_FEATURE_PREFS,
              payload: {
                aiEditorFeaturesEnabled: isChecked,
              },
            });
          }

          return { response };
        } catch (e) {
          logSentryError(e);
          showOnAiWritingAssistantFlag(true);
        }
        return { response };
      };
    }, [
      aiPreferencesCallout,
      workspaceSlug,
      dispatch,
      onAiWritingAssistantToggleTrackEvent,
      preferences,
    ]);

  const onRovoChatToggle: AtlassianIntelligencePreferenceOnToggleListener =
    useMemo(() => {
      return async ({ isChecked }) => {
        function showRovoChatFlag(error: boolean) {
          const action = showFlag(
            getAiToggleRovoChatFlag({
              on: isChecked,
              workspace: workspaceSlug,
              error,
            })
          );
          dispatch(action);
        }

        let response = undefined;
        try {
          response = await aiPreferencesCallout({
            aiRovoChatEnabled: isChecked,
          });
          showRovoChatFlag(!response.ok);

          if (response.ok) {
            const contents =
              (await response.json()) as WorkspaceAiPreferenceResourceDataType;
            onAiRovoChatToggleTrackEvent(preferences, contents);

            dispatch({
              type: SYNC_AI_FEATURE_PREFS,
              payload: {
                aiRovoChatEnabled: isChecked,
              },
            });
          }
        } catch (e) {
          logSentryError(e);
          showRovoChatFlag(true);
        }
        return { response };
      };
    }, [
      aiPreferencesCallout,
      dispatch,
      workspaceSlug,
      onAiRovoChatToggleTrackEvent,
      preferences,
    ]);

  const onAgenticPipelinesToggle: AtlassianIntelligencePreferenceOnToggleListener =
    useMemo(() => {
      return async ({ isChecked }) => {
        publishUiEvent({
          action: isChecked ? 'enabled' : 'disabled',
          actionSubject: 'toggle',
          actionSubjectId: 'agenticPipelinesToggle',
          source: 'atlassianIntelligenceSettings',
          attributes: {
            workspaceId: currentWorkspace?.uuid,
          },
        });

        function showAgenticPipelinesFlag(error: boolean) {
          const action = showFlag(
            getAiToggleAgenticPipelinesFlag({
              on: isChecked,
              workspace: workspaceSlug,
              error,
            })
          );
          dispatch(action);
        }

        let response;
        try {
          response = await updateAgenticPipelinesConfiguration(isChecked);
          showAgenticPipelinesFlag(!response.ok);
        } catch (e) {
          capturePipelinesExceptionWithTags(e, {
            segment: 'update-agentic-pipelines-configuration',
          });
          showAgenticPipelinesFlag(true);
        }
        return { response };
      };
    }, [
      currentWorkspace?.uuid,
      dispatch,
      updateAgenticPipelinesConfiguration,
      workspaceSlug,
    ]);

  const onConfirmDeactivation = useMemo(() => {
    return async () => {
      function showErrorFlag() {
        const action = showFlag(
          getAiToggleFlag({
            on: false,
            error: true,
            workspace: workspaceSlug,
          })
        );
        dispatch(action);
      }

      try {
        setIsDeactivateLoading(true);
        const response = await aiCallout(false);

        if (!response.ok) {
          showErrorFlag();
          return;
        }

        const contents =
          (await response.json()) as WorkspaceAiPreferenceResourceDataType;
        setIsActivated(contents.aiEnabled);

        setIsDeactivationConfirmationOpen(false);
        setShowAtlassianIntelligencePreferences(false);

        if (isAgenticStepsEnabled) {
          updateAgenticPipelinesConfiguration(false);
        }

        const action = showFlag(
          getAiToggleFlag({
            on: false,
            error: false,
            workspace: workspaceSlug,
          })
        );
        dispatch(action);

        onAiDisabled(preferences, contents);

        dispatch({
          type: SYNC_AI_FEATURE_PREFS,
          payload: contents,
        });
      } catch (e) {
        logSentryError(e);
        showErrorFlag();
      } finally {
        setIsDeactivateLoading(false);
      }
    };
  }, [
    workspaceSlug,
    dispatch,
    aiCallout,
    isAgenticStepsEnabled,
    onAiDisabled,
    preferences,
    updateAgenticPipelinesConfiguration,
  ]);

  const aiUpsellDescription = (
    <FormattedMessage
      {...messages.atlassianIntelligencePageDescription}
      values={{
        link: chunks => (
          <a href={atlassianIntelligenceUrls.ui.learnMore()}>{chunks}</a>
        ),
      }}
    />
  );

  return (
    <>
      <PageSection>
        <Grid layout="fluid" spacing="comfortable">
          <GridColumn>
            <FocusedPageHeader
              breadcrumbs={<Breadcrumbs settingsHref={settingsHref} />}
            >
              <Header>
                <FormattedMessage
                  {...messages.atlassianIntelligencePageTitle}
                />
                {showAtlassianIntelligencePreferences && (
                  <Button
                    testId="deactivate-button"
                    appearance="primary"
                    onClick={() => {
                      setIsDeactivationConfirmationOpen(true);
                    }}
                  >
                    {intl.formatMessage(messages.deactivateButtonText)}
                  </Button>
                )}
              </Header>
            </FocusedPageHeader>

            {showUpsell ? (
              <PremiumFeatureUpsell
                featureTitle={aiUpsellTitle}
                featureDescription={aiUpsellDescription}
              />
            ) : (
              <>
                <FormattedMessage
                  {...messages.atlassianIntelligencePageDescription}
                  values={{
                    link: chunks => (
                      <a href={atlassianIntelligenceUrls.ui.learnMore()}>
                        {chunks}
                      </a>
                    ),
                  }}
                />
                {!showAtlassianIntelligencePreferences && (
                  <>
                    <AtlassianIntelligenceThingsToKnow>
                      <li>
                        <FormattedMessage
                          {...messages.atlassianIntelligenceThingsToKnowAdditionalFeatures}
                        />
                      </li>
                      <li>
                        <FormattedMessage
                          {...messages.atlassianIntelligenceThingsToKnowDeactivate}
                        />
                      </li>
                    </AtlassianIntelligenceThingsToKnow>
                    <AtlassianIntelligenceTosForm
                      onSubmit={onSubmit}
                      isActivated={isActivated}
                    ></AtlassianIntelligenceTosForm>
                  </>
                )}
                {showAtlassianIntelligencePreferences && (
                  <PreferenceScreenBox>
                    <AtlassianIntelligencePreferencesScreen
                      isRovoChatInitiallyChecked={preferences.aiRovoChatEnabled}
                      isAiWritingAssistantInitiallyChecked={
                        preferences.aiEditorFeaturesEnabled
                      }
                      isAgenticPipelinesInitiallyChecked={
                        agenticPipelinesEnabled
                      }
                      onAiWritingAssistantToggle={onAiWritingAssistantToggle}
                      onRovoChatToggle={onRovoChatToggle}
                      onAgenticPipelinesToggle={onAgenticPipelinesToggle}
                      isAgenticStepsEnabled={isAgenticStepsEnabled}
                      isAgenticWorkspaceToggleEnabled={
                        isAgenticWorkspaceToggleEnabled
                      }
                    ></AtlassianIntelligencePreferencesScreen>
                  </PreferenceScreenBox>
                )}
              </>
            )}
          </GridColumn>
        </Grid>
      </PageSection>

      <ModalTransition>
        {isDeactivationConfirmationOpen && (
          <AtlassianIntelligenceDeactivationModal
            onCancel={() => {
              setIsDeactivationConfirmationOpen(false);
            }}
            isLoading={isDeactivateLoading}
            onConfirm={onConfirmDeactivation}
          ></AtlassianIntelligenceDeactivationModal>
        )}
      </ModalTransition>
    </>
  );
};

export function useWorkspaceAiPreferences() {
  const isAiEnabled = useSelector(getIsAiEnabled);
  const isAiEditorFeaturesEnabled = useSelector(getIsAiEditorFeaturesEnabled);
  const isAiRovoChatEnabled = useSelector(getIsAiRovoChatEnabled);

  return useMemo<WorkspaceAiPreferenceResourceDataType>(
    () => ({
      aiEnabled: isAiEnabled,
      aiEditorFeaturesEnabled: isAiEditorFeaturesEnabled,
      aiRovoChatEnabled: isAiRovoChatEnabled,
    }),
    [isAiEnabled, isAiEditorFeaturesEnabled, isAiRovoChatEnabled]
  );
}
